import { deriveFlowType } from '@tn/foundation/entities/abtests/utils/deriveAbTestsExtraParams';
import { getRouteWithRegion } from '@tn/foundation/hooks/routes/useRegionRoute';
import { HOME, UNDEFINED } from '@tn/foundation/utils/constants/common';
import { getDomain } from '@tn/foundation/utils/domains';
import { getClientLOB } from '@tn/foundation/utils/server/lob-utils';
import {
    and,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    not,
    or,
    setup,
    stateIn,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import {
    clearRefererUrlFromSession,
    persistRefererUrlToSession,
    readRefererUrlFromSession,
} from '../../utils/redirectUrlSessionStorage';
import { CheckEmailFlowOutput } from '../checkEmail';
import { DTC_EVENTS } from '../dtc/events';
import {
    getErrorMessageFromEvent,
    getLegacyCampaignCookieContent,
    getLegacyFlowCookieContent,
    getUserGuards,
    isRouteSyncEventGuard,
    isStateGuard,
} from '../helpers';
import { MvpdBindOutput, MvpdFlowContext } from '../mvpd';
import { getEnabledMvpdSignInHandoff } from '../mvpd/signInHandoff';
import {
    consumePartnerSignInDestination,
    getEnabledPartnerBindContext,
    getPartnerBindErrorCopy,
    PartnerBindOutput,
} from '../partner';
import { persistEmailToSession, readEmailFromSession } from '../ptvSisu/emailSession';
import {
    clearRendezvousSignInOnlyFlag,
    getEnabledRendezvousSignInHandoff,
    RendezvousSignInHandoff,
} from '../rendezvous/signInHandoff';
import { SignInFlowContext } from '../signin';
import { shouldHideMvpdCta } from '../signin/hideMvpdCta';
import { SignUpFlowContext } from '../signup';
import { getFeatureFlagGuards } from '../utils/getFeatureFlagGuards';
import { getValidRedirectUrl } from '../utils/getValidRedirectUrl';

type DtcFlowContext = {
    email: string;
    redirectUrl: string | null;
    refererUrl: string | null;
    legacyFlowCookie: ReturnType<typeof getLegacyFlowCookieContent>;
    legacyCampaignCookie: ReturnType<typeof getLegacyCampaignCookieContent>;
    /** Signin form's imperative API, saved on the mvpdBind handoff so bind errors can banner on the still-mounted form. */
    signInFormBuilderRef: SignInFlowContext['formBuilderRef'];
    /**
     * Legacy SignIn.vue displayMVPDConnectCTA parity, detected on signIn
     * entry (detectHideMvpdCta). Single source of truth for the sign-in
     * page's TV-provider block (via selectHideMvpdCta) — components never
     * read the query/storage signals themselves.
     */
    hideMvpdCta: boolean;
    /**
     * Rendezvous handoff, detected on signIn entry
     * (detectRendezvousSignInHandoff). Single source of truth for the
     * destination decision and the sign-in page UI (via
     * selectRendezvousSignInHandoff) — components never read the
     * cookies/storage signals themselves.
     */
    rendezvousSignInHandoff: RendezvousSignInHandoff | null;
};

/**
 * The live-tv and edu-offer signup paths rewrite to `/?cbscidmt=…`
 * (`buildAppShellUpsellRewrites`): the page route is `/`, the browser keeps the
 * source path, which the upsell state's `/` equality check never matches.
 */
const isRewrittenUpsellPath = () => {
    if (typeof globalThis.window === UNDEFINED) return false;
    const flowType = deriveFlowType(globalThis.location.pathname, getClientLOB());
    return flowType === 'livetv' || flowType === 'edu';
};

export const dtcFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router, getConfigByKey, queryClient, region, t } =
            rawParams as InitMachineParams;
        const isState = isStateGuard({ getConfigByKey, flowId: 'dtc', router, region });
        const userGuards = getUserGuards(queryClient);
        const featureFlagGuards = getFeatureFlagGuards(queryClient);

        const routeWithRegion = (route: string, isSubscriber: boolean) => {
            const includeRegionForNonSubscriber = !!getConfigByKey(
                'options.includeRegionForNonSubscriber',
            );
            const alwaysIncludeRegion = !!getConfigByKey('options.alwaysIncludeRegion');
            return getRouteWithRegion({
                includeRegionForNonSubscriber,
                alwaysIncludeRegion,
                isSubscriber,
                baseUrl: getDomain(),
                region: region,
            })(route, false);
        };

        const navigateWithRegion = (route: string, isSubscriber: boolean) => {
            window.location.href = routeWithRegion(route, isSubscriber);
        };

        /**
         * The legacy rendezvous complete page exists only region-prefixed in
         * INTL (routes.<region>-generated.php registers `/gb/tv/complete`, never
         * a bare `/tv/complete`), so it must not take the P+ subscriber
         * drop-the-region shortcut — legacy builds this URL with
         * `UrlHelper::url()`, which always prefixes. Passing isSubscriber=false
         * defers to includeRegionForNonSubscriber, which is exactly the split we
         * need: INTL prefixes, US does not (its `/activate/tv/complete` is
         * registered unprefixed).
         */
        const navigateToRendezvousComplete = (route: string) => {
            navigateWithRegion(route, false);
        };

        return setup({
            types: {
                context: {} as DtcFlowContext,
                events: {} as
                    | DoneActorEvent<undefined, 'upsell'>
                    | DoneActorEvent<CheckEmailFlowOutput, 'checkEmail'>
                    | DoneActorEvent<SignInFlowContext, 'signIn'>
                    | DoneActorEvent<SignUpFlowContext, 'signUp'>
                    | DoneActorEvent<MvpdFlowContext, 'mvpd'>
                    | DoneActorEvent<MvpdBindOutput, 'mvpdBind'>
                    | ErrorActorEvent<unknown, 'mvpdBind'>
                    | DoneActorEvent<PartnerBindOutput, 'partnerBind'>
                    | { type: 'xstate.init' }
                    | { type: 'POP_STATE' }
                    | { type: 'ROUTE_CHANGE' }
                    | { type: 'ALREADY_LOGGED_IN' }
                    | { type: 'CHECK_EMAIL' }
                    | { type: 'SAVE_REDIRECT_URL'; params: string }
                    | { type: 'SAVE_REFERER_URL'; params: string }
                    | { type: 'SAVE_REFERER_URL_ONCE'; params: string }
                    | { type: 'SIGN_IN' }
                    | { type: 'SIGN_UP' }
                    | { type: DTC_EVENTS.CLICK_SIGNIN }
                    | { type: DTC_EVENTS.CLICK_SIGNUP }
                    | { type: DTC_EVENTS.CLICK_MVPD }
                    | { type: DTC_EVENTS.CLICK_FORGOT_PASSWORD },
            },
            guards: {
                isCheckEmailRoute: and(isState('checkEmail', 'inclusion')),
                isCheckEmail: and(['isCheckEmailRoute', 'unifiedIdentityPlatformEnabled']),
                isSignIn: and(isState('signIn', 'inclusion')),
                isSignUp: and(isState('signUp', 'inclusion')),
                isMvpd: and(isState('mvpd', 'inclusion')),
                isUpsell: or([
                    and(isState('upsell', 'equality')),
                    and([isRouteSyncEventGuard, not(stateIn('upsell')), isRewrittenUpsellPath]),
                ]),
                isForgotPassword: and(isState('forgotPassword', 'inclusion')),
                // P+ only (blueprint option; PTV has neither the option nor the
                // config branch): a done sign-in with the legacy MVPD handoff
                // present continues into the mvpdBind state instead of the
                // normal destination decision.
                shouldBindMvpdOnSignIn: () => getEnabledMvpdSignInHandoff(getConfigByKey) !== null,
                // Legacy parity for an already-authenticated visitor with the
                // handoff: registered/ex-subscribers bind without a sign-in
                // submit (Mvpd.vue checkBinding / SignIn.vue authSuiteTokens
                // watcher both call registerWithApi directly); subscribers are
                // sent away unbound (PHP redirectIfAuthorized), i.e. the
                // normal destination decision.
                shouldBindMvpdWhenAlreadyLoggedIn: () =>
                    getEnabledMvpdSignInHandoff(getConfigByKey) !== null &&
                    !userGuards.isSubscriber(),
                // P+ only (blueprint option): a done sign-in with the legacy
                // billing-partner sessionStorage flow present continues into the
                // partnerBind state instead of the normal destination decision.
                shouldBindPartnerOnSignIn: () =>
                    getEnabledPartnerBindContext(getConfigByKey) !== null,
                partnerBindFailed: ({ event }) =>
                    event.type === 'xstate.done.actor.partnerBind' && !!event.output.errorCode,

                ...userGuards,
                ...featureFlagGuards,
            },
            actions: {
                // Runs on signIn entry. NB: initial-state entry assigns run in
                // the initial snapshot, i.e. during the hydration render —
                // components reading this must gate on first mount to match
                // the SSR markup (the server can't see the client-only
                // query/storage signals).
                detectHideMvpdCta: enqueueActions(({ enqueue }) => {
                    enqueue.assign({ hideMvpdCta: shouldHideMvpdCta(getConfigByKey) });
                }),
                checkEmailInSession: enqueueActions(({ enqueue, context, check }) => {
                    if (!check({ type: 'unifiedIdentityPlatformEnabled' })) {
                        return;
                    }
                    if (check({ type: 'hasCookie' })) {
                        return;
                    }
                    let email: string | null = context.email;

                    if (!email) {
                        email = readEmailFromSession();

                        if (email) {
                            enqueue.assign({ email: email || '' });
                        } else {
                            enqueue.raise({ type: 'CHECK_EMAIL' });
                        }
                    }
                }),
                checkRefererUrlInSession: enqueueActions(({ enqueue, context, check }) => {
                    if (!check({ type: 'unifiedIdentityPlatformEnabled' })) {
                        return;
                    }
                    if (!context.refererUrl) {
                        const validRefererUrl = readRefererUrlFromSession();
                        enqueue.assign({ refererUrl: validRefererUrl });
                    }
                }),
                checkUserStatus: enqueueActions(({ enqueue, check }) => {
                    if (check({ type: 'hasCookie' })) {
                        enqueue.raise({ type: 'ALREADY_LOGGED_IN' });
                    }
                }),
                // Runs on signIn entry, before checkUserStatus can raise
                // ALREADY_LOGGED_IN, so decideSignInDestination always sees
                // the detected handoff. NB: initial-state entry assigns run in
                // the initial snapshot, i.e. during the hydration render —
                // components reading this must gate on first mount to match
                // the SSR markup (the server can't see the flow cookie).
                detectRendezvousSignInHandoff: enqueueActions(({ enqueue }) => {
                    enqueue.assign({
                        rendezvousSignInHandoff: getEnabledRendezvousSignInHandoff(getConfigByKey),
                    });
                }),
                decideUpsellDestination: enqueueActions(({ check, event }) => {
                    assertEvent(event, ['ALREADY_LOGGED_IN']);

                    if (check({ type: 'isSubscriber' })) {
                        const route = <string>getConfigByKey('dtc.destinationUrls.subscriber');
                        if (route) router.push(route);
                    }
                }),
                goToLegacySignIn: enqueueActions(({ context }) => {
                    const redirectUrl = getValidRedirectUrl(context.redirectUrl);

                    if (redirectUrl) {
                        navigateWithRegion(
                            `/account/signin/?redirectUrl=${encodeURIComponent(redirectUrl)}`,
                            false,
                        );
                        return;
                    }

                    navigateWithRegion('/account/signin/', false);
                }),
                goToFirstSignUpStep: enqueueActions(() => {
                    const fallbackSignUpUrl = '/account/user-flow/f-upsell/'; // loses original legacy flow type if any, prefer entering at first route
                    const signUpUrl =
                        <string>getConfigByKey('dtc.destinationUrls.signUp') || fallbackSignUpUrl;

                    navigateWithRegion(signUpUrl, false);
                }),
                handleCheckEmailCompletion: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.checkEmail');

                    enqueue.assign({ email: event.output.email });

                    if (event.output.email) {
                        persistEmailToSession(event.output.email);
                    }

                    switch (event.output.destinationState) {
                        case 'signIn':
                            enqueue.raise({ type: 'SIGN_IN' });
                            break;
                        case 'signUp':
                            enqueue.raise({ type: 'SIGN_UP' });
                            break;
                        default:
                            break;
                    }
                }),
                saveRedirectUrl: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'SAVE_REDIRECT_URL');
                    enqueue.assign({
                        redirectUrl: event.params,
                    });
                }),
                saveRefererUrl: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'SAVE_REFERER_URL');
                    enqueue.assign({
                        refererUrl: event.params,
                    });
                }),
                saveRefererUrlOnce: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'SAVE_REFERER_URL_ONCE');

                    if (!context.refererUrl && event.params) {
                        const validRefererUrl = getValidRedirectUrl(event.params);
                        if (validRefererUrl) {
                            enqueue.assign({
                                refererUrl: validRefererUrl,
                            });
                            persistRefererUrlToSession(validRefererUrl);
                        }
                    }
                }),
                decideSignInDestination: enqueueActions(
                    ({ enqueue: _enqueue, check, event, context }) => {
                        assertEvent(event, ['xstate.done.actor.signIn', 'ALREADY_LOGGED_IN']);

                        // Rendezvous handoff subscribers complete the device
                        // activation instead of following the referer/home —
                        // legacy standalone sign-in parity (__sub →
                        // flow/destinationUrl). Non-subscribers fall through to
                        // the plan funnel below; the untouched graph.svod cookie
                        // returns them to rendezvous complete after purchase.
                        const rendezvousRoute = context.rendezvousSignInHandoff
                            ? <string>getConfigByKey('dtc.destinationUrls.rendezvousComplete')
                            : undefined;
                        // Legacy parity: any successful sign-in clears the
                        // rendezvous sign-in-only flag (SignIn.vue
                        // clearStoredActivationScreen). Form sign-ins already
                        // cleared it in saveDataFromSuccessfulSignin; this covers
                        // the ALREADY_LOGGED_IN arrival.
                        clearRendezvousSignInOnlyFlag();
                        clearRefererUrlFromSession();

                        const redirectUrl = getValidRedirectUrl(context.redirectUrl);
                        const refererUrl = getValidRedirectUrl(context.refererUrl);

                        if (redirectUrl) {
                            globalThis.location.href = redirectUrl;
                        } else if (rendezvousRoute && check({ type: 'isSubscriber' })) {
                            navigateToRendezvousComplete(rendezvousRoute);
                        } else if (refererUrl && check({ type: 'isSubscriber' })) {
                            globalThis.location.href = refererUrl;
                        } else {
                            const BASE_PATH = 'dtc.destinationUrls';

                            let route: string | undefined;
                            if (check({ type: 'isSubscriber' })) {
                                route = <string>getConfigByKey(`${BASE_PATH}.subscriber`);
                            } else if (check({ type: 'isExSubscriber' })) {
                                route = <string>getConfigByKey(`${BASE_PATH}.exSubscriber`);
                            } else if (check({ type: 'isRegistered' })) {
                                route = <string>getConfigByKey(`${BASE_PATH}.registered`);
                            }

                            if (route) {
                                navigateWithRegion(route, check({ type: 'isSubscriber' }));
                            }
                        }
                    },
                ),
                saveSignInFormRef: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.signIn');
                    enqueue.assign({ signInFormBuilderRef: event.output.formBuilderRef });
                }),
                decideMvpdBindDestination: enqueueActions(({ event, context }) => {
                    assertEvent(event, 'xstate.done.actor.mvpdBind');
                    // Legacy parity: any successful sign-in clears the rendezvous
                    // sign-in-only flag — this covers the ALREADY_LOGGED_IN bind,
                    // which never passes through saveDataFromSuccessfulSignin.
                    clearRendezvousSignInOnlyFlag();
                    // Defensive (the actor resolves bound:false only when the
                    // handoff vanished between the guard and the actor): no
                    // bind ran, so don't fake the subscriber redirect — and
                    // drop the overlay the handoff sign-in kept up.
                    if (!event.output.bound) {
                        context.signInFormBuilderRef?.toggleLoading(false);
                        return;
                    }
                    // Legacy registerWithApi parity: bind success hard-redirects
                    // without consulting user status (which is stale right after
                    // the bind anyway). A pending rendezvous handoff still wins —
                    // the just-bound user completes the device activation instead
                    // of landing on home with the TV screen left waiting.
                    const rendezvousRoute = context.rendezvousSignInHandoff
                        ? <string>getConfigByKey('dtc.destinationUrls.rendezvousComplete')
                        : undefined;
                    if (rendezvousRoute) {
                        navigateToRendezvousComplete(rendezvousRoute);
                        return;
                    }
                    const route = <string>getConfigByKey('dtc.destinationUrls.subscriber');
                    if (route) {
                        navigateWithRegion(route, true);
                    }
                }),
                handleMvpdBindError: enqueueActions(({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.mvpdBind');
                    // The sign-in itself succeeded, so the sign-in-only flag is
                    // cleared here too (legacy clears it regardless of the bind).
                    clearRendezvousSignInOnlyFlag();
                    // Bind failed but the sign-in succeeded — banner the mapped
                    // legacy copy on the still-mounted signin form and park; no
                    // redirect (legacy also stays on the page). The overlay was
                    // kept up through the bind (saveDataFromSuccessfulSignin
                    // skips the toggle on a handoff) — drop it with the banner.
                    context.signInFormBuilderRef?.toggleLoading(false);
                    context.signInFormBuilderRef?.toggleBanner({
                        message: getErrorMessageFromEvent(
                            event as Parameters<typeof getErrorMessageFromEvent>[0],
                        ),
                        variant: 'critical',
                    });
                }),
                decidePartnerBindDestination: enqueueActions(({ event }) => {
                    assertEvent(event, 'xstate.done.actor.partnerBind');
                    // Legacy PartnerStore bindPartnerSuccessCallback parity:
                    // own-devices partners go to rendezvous activation, others go
                    // home; consuming the destination clears the partner flow.
                    // The rendezvous route is region-prefixed on INTL and bare on
                    // US (`/us/tv` does not exist), which is exactly the
                    // non-subscriber rule — the profile is still the pre-bind
                    // (anonymous) one here anyway.
                    const destination = consumePartnerSignInDestination((route) =>
                        routeWithRegion(route, false),
                    );
                    window.location.href = destination ?? '/';
                }),
                handlePartnerBindError: enqueueActions(({ event, context }) => {
                    assertEvent(event, 'xstate.done.actor.partnerBind');
                    // Legacy showPartnerError parity: blocking modal on the
                    // still-mounted signin form, the only way out is Home (the
                    // user is signed in but not bound). The machine parks in the
                    // partnerBind state, so no redirect fires. The overlay the
                    // handoff sign-in kept up drops with the modal.
                    context.signInFormBuilderRef?.toggleLoading(false);
                    const { title, message } = getPartnerBindErrorCopy(
                        t,
                        event.output.errorCode ?? '',
                        event.output.displayName ?? '',
                    );
                    context.signInFormBuilderRef?.toggleModal?.({
                        title,
                        message,
                        confirmLabel: t('close', null, 'Close'),
                        dismissible: false,
                        onConfirm: () => {
                            window.location.href = HOME;
                        },
                        'data-ci': 'partnerBindErrorModal',
                    });
                }),
                decideSignUpDestination: enqueueActions(
                    ({ enqueue: _enqueue, check, event, context }) => {
                        assertEvent(event, ['xstate.done.actor.signUp', 'ALREADY_LOGGED_IN']);

                        if (context.redirectUrl) {
                            window.location.href = context.redirectUrl;
                        } else {
                            const BASE_PATH = 'dtc.destinationUrls';

                            if (check({ type: 'isSubscriber' })) {
                                const route = <string>getConfigByKey(`${BASE_PATH}.subscriber`);
                                if (route) router.push(route);
                            } else if (check({ type: 'isExSubscriber' })) {
                                const route = <string>getConfigByKey(`${BASE_PATH}.exSubscriber`);
                                if (route) router.push(route);
                            } else if (check({ type: 'isRegistered' })) {
                                const route = <string>getConfigByKey(`${BASE_PATH}.registered`);
                                if (route) router.push(route);
                            }
                        }
                    },
                ),
                decideMvpdDestination: enqueueActions(() => {
                    // placeholder for future use
                }),
                decideForgotPasswordDestination: enqueueActions(() => {
                    // Forgot Password placeholder for future logic
                }),
            },
            actors,
        }).createMachine({
            /** @xstate-layout N4IgpgJg5mDOIC5gF8A0IB2B7CdGgAoBbAQwGMALASwzAEp8QAHLWKgFyqw0YA9EAjACZ0AT0FDkU5EA */ ...(config as AnyStateConfig),
            context: {
                email: '',
                redirectUrl: null,
                refererUrl: null,
                signInFormBuilderRef: null,
                hideMvpdCta: false,
                rendezvousSignInHandoff: null,
                ...(config?.context ?? {}),
                legacyFlowCookie: getLegacyFlowCookieContent(),
                legacyCampaignCookie: getLegacyCampaignCookieContent(),
            },
        });
    },
};
